import { useState, useCallback, useEffect, useRef, memo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Building2, ChevronDown, FileText, Home, LineChart, Shield, Sprout, Activity, BookOpen, PencilLine, CreditCard, Languages, ScrollText } from 'lucide-react';
import { NavigationItem } from '../types';
import { useProcessing } from '../context/ProcessingContext';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import clsx from 'clsx';

const navigationItems: NavigationItem[] = [
  { id: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: 'Home' },
  { id: 'my-drafts', name: 'My Drafts', href: '/my-drafts', icon: 'ScrollText' },
  { id: 'paper-assistant', name: 'Paper Assistant', href: '/paper-assistant', icon: 'PencilLine' },
  { id: 'translator', name: 'Translator', href: '/translator', icon: 'Languages' },
  { id: 'blog', name: 'Blog', href: '/blog', icon: 'BookOpen' },
  { id: 'credit-history', name: 'Credit History', href: '/credit-history', icon: 'CreditCard' },
];

const iconComponents = {
  Home,
  LineChart,
  FileText,
  Building2,
  Shield,
  Activity,
  Sprout,
  BookOpen,
  PencilLine,
  CreditCard,
  Languages,
  ScrollText,
};

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  hasUnsavedChanges?: boolean;
}

const Sidebar = memo(({ isOpen, onToggle, hasUnsavedChanges = false }: SidebarProps) => {
  const { user } = useAuth();
  const { isProcessing } = useProcessing();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const handleNavigate = useCallback((href: string) => {
    if (isProcessing || href === '#') {
      return;
    }

    if (hasUnsavedChanges) {
      const shouldLeave = window.confirm('Are you sure you want to leave? Your changes will be lost.');
      if (!shouldLeave) {
        return;
      }
    }

    navigate(href);
    // Removed onToggle() - sidebar will only close when clicking outside or using the toggle button
    setExpandedItems([]);
  }, [navigate, isProcessing, hasUnsavedChanges]);

  const toggleExpanded = useCallback((itemId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    // Removed automatic sidebar opening - only the dedicated toggle button should control sidebar state
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        onToggle();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  useEffect(() => {
    setExpandedItems([]);
  }, [location.pathname]);

  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) {
        onToggle();
      }
    }

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, onToggle]);

  const renderNavItem = useCallback((item: NavigationItem, depth = 0) => {
    const Icon = iconComponents[item.icon as keyof typeof iconComponents] || Activity;
    const isActive = location.pathname === item.href;

    if (item.children) {
      return (
        <div key={item.id}>
          <button
            onClick={(e) => !isProcessing && toggleExpanded(item.id, e)}
            disabled={isProcessing}
            className={clsx(
              "w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-left",
              isActive
                ? "bg-[--state-selected] text-[--link-default]"
                : "text-[--text-primary] hover:bg-[--state-hover]",
              isProcessing && "opacity-50"
            )}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            {isOpen && (
              <>
                <span className="ml-3 flex-1">{item.name}</span>
                <ChevronDown
                  className={clsx(
                    "w-4 h-4 transform transition-transform",
                    expandedItems.includes(item.id) && "rotate-180"
                  )}
                />
              </>
            )}
          </button>
          {isOpen && expandedItems.includes(item.id) && (
            <div className="ml-6 mt-1 space-y-1">
              {item.children.map((child) => renderNavItem(child, depth + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={item.id} className="relative group">
        <button
          onClick={() => !isProcessing && handleNavigate(item.href)}
          disabled={isProcessing}
          className={clsx(
            "w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-left",
            isActive
              ? "bg-[--state-selected] text-[--link-default]"
              : "text-[--text-primary] hover:bg-[--state-hover]",
            isProcessing && "opacity-50"
          )}
        >
          <Icon className="w-5 h-5 flex-shrink-0" />
          {isOpen && (
            <span className="ml-3 flex-1">{item.name}</span>
          )}
        </button>
      </div>
    );
  }, [isOpen, isProcessing, expandedItems, location.pathname, handleNavigate, toggleExpanded]);

  if (!user) return null;

  return (
    <>
      {isOpen && (
        <div
          ref={overlayRef}
          className="fixed inset-0 bg-[rgb(var(--color-black-rgb)/0.5)] z-20"
          aria-hidden="true"
        />
      )}

      <div
        ref={sidebarRef}
        className={clsx(
          "fixed left-0 top-16 h-[calc(100vh-4rem)] bg-[--bg-primary] border-r border-[--border-primary] transition-all duration-300 z-30",
          isOpen ? "w-64" : "w-16"
        )}
      >
        <div className="p-2">
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-between p-2 hover:bg-[--state-hover] rounded-lg"
          >
            <span className={clsx("font-semibold text-[--text-primary]", !isOpen && "hidden")}>
              Navigation
            </span>
            <ChevronDown
              className={clsx(
                "w-5 h-5 text-[--text-secondary] transform transition-transform",
                isOpen ? "rotate-0" : "-rotate-90"
              )}
            />
          </button>
        </div>

        <nav className="space-y-1 px-2 overflow-y-auto h-[calc(100%-3.5rem)] scrollbar-hide">
          {navigationItems.map((item) => renderNavItem(item))}
        </nav>
      </div>
    </>
  );
});

Sidebar.displayName = 'Sidebar';

export { Sidebar };